import { useMemo } from 'react';
import Countdown from '../components/Countdown';
import ObjectiveStateResource from '../resources/ObjectiveState';
import AllUserProfilesResource from '../resources/AllUserProfiles';
import BallotsScoresResource from '../resources/Ballot/Scores';
import optionTypes from '../resources/Ballot/constants/optionTypes';
import { makeClassName } from '@siteless/util';
import useCopy from '../util/useCopy';
import useLocale from '../util/useLocale';
import { formatCeremonyDate } from '../util/formatCeremonyDate';
import { BallotPreviewItem } from './BallotPreviewItem';
import events from '../resources/Ballot/constants/events';
import CalendarIcon from './CalendarIcon';

// TODO: remove after 2026 — Live Action Short tie override
let LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES = new Set([
  'iTZ1eyNVdCrdPfyR2fpoh', // The Singers
  'rLEc89MBBOh2EGXj-dKwG', // Two People Exchanging Saliva
]);

export let CeremonyDetailCard = ({ className }: { className?: string }) => {
  let copy = useCopy();
  let locale = useLocale();

  let [allUserProfiles] = AllUserProfilesResource.useLoadable();
  let ballotScores = BallotsScoresResource.use();
  let objectiveState = ObjectiveStateResource.use();

  let eventPointsMap = useMemo(() => {
    let eventPointsMap: {
      [eventName: string]: {
        type: string;
        section: string;
        displayName: string;
        points: number;
      }[];
    } = {};

    Object.entries(objectiveState.ballotShape).forEach(
      ([sectionKey, section]) => {
        if (!section || !section.options) return;
        Object.entries(section.options).forEach(([optionKey, option]) => {
          if (!option || !option.type || !option.data) return;
          if (
            option.type === optionTypes.eventCount.key ||
            option.type === optionTypes.eventBoolean.key
          ) {
            const eventName = option.data.eventName;
            const points = option.data.points || 0;

            if (!eventName) return;

            if (!eventPointsMap[eventName]) {
              eventPointsMap[eventName] = [];
            }

            eventPointsMap[eventName].push({
              type: option.type,
              section: sectionKey,
              displayName: section.displayName || sectionKey,
              points: points,
            });
          }
        });
      },
    );

    return eventPointsMap;
  }, [objectiveState]);

  let winner = useMemo(() => {
    let scoredBallots = Object.entries(ballotScores)
      .map(([createdByUserId, score]) => ({
        score,
        handle: allUserProfiles?.users[createdByUserId]?.handle,
      }))
      .filter(({ handle }) => handle)
      .sort((a, b) => b.score.score - a.score.score);

    if (!scoredBallots[0]) return;

    let highScore = scoredBallots[0].score.score;

    return {
      handles: scoredBallots
        .filter(({ score }) => score.score === highScore)
        .map(({ handle }) => handle)
        .filter((handle) => typeof handle === 'string') as string[],
      points: highScore,
    };
  }, [allUserProfiles, ballotScores]);

  let top = useMemo(() => {
    if (objectiveState.didCeremonyEnd && winner) return null;

    if (!objectiveState.didCeremonyStart)
      return {
        topLabel: copy.theOscarsAreLiveIn,
        topBody: Boolean(objectiveState?.ceremonyStartAt) && (
          <Countdown
            toTimestamp={objectiveState?.ceremonyStartAt.seconds * 1000}
          />
        ),
        topSubtitle: objectiveState?.ceremonyStartAt
          ? formatCeremonyDate(objectiveState.ceremonyStartAt.seconds, locale)
          : undefined,
      };

    let lastResult =
      objectiveState.results[objectiveState?.results.length - 1] || {};

    let lastResultCategoryKey = Object.keys(lastResult)[0];
    let lastResultFilmKey = Object.values(lastResult)[0] as string | undefined;

    let lastResultCategory =
      objectiveState.nomineeCategories[
        // artificially allow indexing with string
        lastResultCategoryKey as keyof typeof objectiveState.nomineeCategories
      ];

    let lastResultWinner = (lastResultCategory && lastResultFilmKey
      ? lastResultCategory.nominees[lastResultFilmKey] || null
      : null) as unknown as {
      id: string;
      detail?: string;
      filmId: string;
      peopleIds: string[];
    };

    if (lastResultCategory && lastResultWinner) {
      let optionComponentProps = optionTypes.winner.getOptionComponentProps({
        copy,
        objectiveState,
        data: {
          // @ts-expect-error artificially allow indexing with string
          categoryKey:
            lastResultCategory.key as keyof typeof objectiveState.nomineeCategories,
          nomineeId: lastResultWinner.id,
          points: 0, // DUMMY
        },
      });
      return {
        topLabel: lastResultCategory.title,
        topBody: (
          <>
            {`${optionComponentProps.title} (${optionComponentProps.details})`}
          </>
        ),
      };
    }

    return {
      topLabel: copy.nextCategory,
      topBody: lastResultCategory?.title || copy.toBeAnnounced,
    };
  }, [objectiveState, copy, winner, locale]);

  return (
    <div
      className={makeClassName(
        'bg-neutral-0 relative flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border-4 border-neutral-200 pt-3 pb-2 text-purple-900 shadow-lg',
        className,
      )}
    >
      {top && (
        <div className="flex-none bg-white">
          <div className="mx-4 border-b-4 border-neutral-100 pt-2 pb-4 text-purple-900">
            <div className="mb-1 truncate text-sm font-medium uppercase text-rose-default">
              {top.topLabel}
            </div>
            <div className="font-medium">{top.topBody}</div>
            {top.topSubtitle && (
              <div className="mt-1 flex items-center gap-1 text-sm text-neutral-500">
                <CalendarIcon className="h-4 w-4 shrink-0" />
                <span>{top.topSubtitle}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {!objectiveState.didCeremonyStart && (
        <div className="flex-none py-6 px-8 text-center text-neutral-500">
          Winners will be displayed here after the ceremony starts.
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        {/* TODO: remove augmentedResults after 2026 — Live Action Short tie override */}
        {(() => {
          let augmentedResults = [...objectiveState.results];
          for (let i = 0; i < augmentedResults.length; i++) {
            let val = Object.values(augmentedResults[i])[0];
            if (LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES.has(val as string)) {
              let catKey = Object.keys(augmentedResults[i])[0];
              let other = Array.from(LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES).find(id => id !== val);
              if (other) augmentedResults.splice(i + 1, 0, { [catKey]: other });
              break;
            }
          }
          return augmentedResults;
        })().toReversed().map((result) => {
          let [key, value] = Object.entries(result)[0];
          let title = null;
          let selectedOption = null;

          for (let ballotSectionKey in objectiveState.ballotShape) {
            // @ts-expect-error
            for (let ballotSectionOptionKey in objectiveState.ballotShape[
              ballotSectionKey
            ].options) {
              if (
                // @ts-expect-error
                objectiveState.ballotShape[ballotSectionKey].options[
                  ballotSectionOptionKey
                ]?.data?.nomineeId === value
              ) {
                title =
                  // @ts-expect-error
                  objectiveState.ballotShape[ballotSectionKey].displayName;
                selectedOption =
                  // @ts-expect-error
                  objectiveState.ballotShape[ballotSectionKey].options[
                    ballotSectionOptionKey
                  ];
                break;
              }
            }
          }
          if (!value || !title) return null;
          // TODO: remove after 2026 — Live Action Short tie override
          let isTie = LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES.has(value as string);
          return (
            <div key={key + '-' + value} className="mx-4 mt-4">
              <BallotPreviewItem
                ballotItemDisplayName={isTie ? `${title} (Tie)` : title}
                selectedOption={selectedOption}
              />
            </div>
          );
        })}
      </div>
      {objectiveState.didCeremonyStart && (
        <div className="flex gap-2 py-2">
          {Object.keys(events)
            // @ts-expect-error
            .map((key: keyof typeof events) => {
              let { recurring, getTitle } = events[key];

              let points = eventPointsMap[key];

              if (
                recurring ||
                (!recurring && Boolean(objectiveState.events[key]))
              ) {
                return (
                  <div
                    className="flex min-w-0 flex-1 flex-col items-end gap-1 rounded-xl bg-neutral-100 px-3 py-2 text-right"
                    key={key}
                  >
                    <optionTypes.eventCount.Icon className="inline-block h-4 w-4 flex-none text-neutral-400" />
                    <div className="flex-1 text-sm font-medium">
                      {getTitle(copy)}
                    </div>
                    <div className="text-md font-bold">
                      {points.map((point, index) => {
                        let points = recurring
                          ? ((objectiveState.events[key] as number) || 0) *
                            point.points
                          : point.points;
                        return (
                          <div
                            key={index}
                            className={
                              points > 0
                                ? 'text-rose-default'
                                : 'text-neutral-500'
                            }
                          >
                            {points > 0 && '+ '} {points}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              return null;
            })}
        </div>
      )}
    </div>
  );
};
